import { useAuthStore } from '../stores/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, User, Bell, Wallet, Menu, ArrowRightLeft, Repeat, FileText, GraduationCap, DollarSign, ShoppingCart, Megaphone, Shield, X, ChevronRight } from 'lucide-react';
import { useState } from 'react';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const user = useAuthStore((state) => state.user);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const menuItems = [
    { icon: Wallet, label: '儀表板', path: '/dashboard' },
    { icon: ArrowRightLeft, label: '轉帳', path: '/transfer' },
    { icon: Repeat, label: '兌換', path: '/exchange' },
    { icon: FileText, label: '存摺', path: '/passbook' },
    { icon: GraduationCap, label: '成績', path: '/grades' },
    { icon: DollarSign, label: '班費', path: '/class-fees' },
    { icon: ShoppingCart, label: '販賣部', path: '/shop' },
    { icon: Megaphone, label: '公告', path: '/announcements' },
    { icon: Bell, label: '通知', path: '/notifications' },
    { icon: User, label: '個人資料', path: '/profile' },
  ];

  const adminItems = user?.role === 'admin' ? [
    { icon: Shield, label: '管理後台', path: '/admin' },
  ] : [];

  const allMenuItems = [...menuItems, ...adminItems];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? <X className="h-6 w-6 text-slate-700" /> : <Menu className="h-6 w-6 text-slate-700" />}
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Wallet className="h-5 w-5 text-white" />
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  數資銀行
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-sm font-medium text-slate-600">{user?.name}</span>
              <button 
                onClick={handleLogout}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <LogOut className="h-5 w-5 text-slate-600" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex max-w-7xl mx-auto">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 transition-transform duration-300 ease-in-out lg:block pt-16 lg:pt-0`}
        >
          <div className="h-full flex flex-col">
            <div className="flex-1 py-6 px-4">
              <nav className="space-y-1">
                {allMenuItems.map((item) => (
                  <a
                    key={item.path}
                    href={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
                      isActive(item.path)
                        ? 'bg-slate-900 text-white shadow-lg'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.label}
                    {isActive(item.path) && <ChevronRight className="h-4 w-4 ml-auto" />}
                  </a>
                ))}
              </nav>
            </div>

            {/* Balance Card */}
            <div className="p-4 border-t border-slate-200">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-4 text-white shadow-xl">
                <h3 className="text-sm font-medium text-blue-100 mb-3">我的餘額</h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-2xl font-bold">${user?.ntBalance?.toLocaleString() || 0}</p>
                    <p className="text-xs text-blue-100">新臺幣</p>
                  </div>
                  <div className="pt-2 border-t border-white/20">
                    <p className="text-lg font-bold">
                      {user?.msBalance?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || 0} <span className="text-sm">MS∮</span>
                    </p>
                    <p className="text-xs text-blue-100">數資幣</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 min-h-[calc(100vh-4rem)]">
          <div className="max-w-6xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
